import { useState, useEffect, useCallback, useRef } from 'react';
import { MessageService, UnreadMessagesResponse } from '@/services/messages/messageService';
import { useAuth } from '@/context/AuthContext';

interface UseUnreadMessagesOptions {
  enableRealtime?: boolean;
  pollingInterval?: number;
  autoRefresh?: boolean;
}

interface UseUnreadMessagesReturn {
  count: number;
  isLoading: boolean;
  error: Error | null;
  detail: UnreadMessagesResponse | null;
  refresh: () => Promise<void>;
  markAsRead: (messageId: string) => Promise<void>;
  markConversationAsRead: (conversationId: string) => Promise<void>;
}

export const useUnreadMessages = (
  options: UseUnreadMessagesOptions = {}
): UseUnreadMessagesReturn => {
  const {
    enableRealtime = false, // Disabled by default due to WebSocket connection issues
    pollingInterval = 60000, // 1 minute
    autoRefresh = true
  } = options;

  const { isAuthenticated, user } = useAuth();
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [detail, setDetail] = useState<UnreadMessagesResponse | null>(null);
  
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const realtimeCleanupRef = useRef<(() => void) | null>(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setCount(0);
      setDetail(null);
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      const [unreadCount, unreadDetail] = await Promise.all([
        MessageService.getUnreadMessageCount(),
        MessageService.getUnreadMessagesDetail()
      ]);
      
      setCount(unreadCount);
      setDetail(unreadDetail);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch unread messages');
      setError(error);
      console.error('[useUnreadMessages] Error fetching unread count:', error);
      // Don't reset count on error, keep previous value
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await fetchUnreadCount();
  }, [fetchUnreadCount]);

  const markAsRead = useCallback(async (messageId: string) => {
    try {
      await MessageService.markMessageAsRead(messageId);
      // Refresh count after marking as read
      await fetchUnreadCount();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to mark message as read');
      setError(error);
      console.error('[useUnreadMessages] Error marking message as read:', error);
    }
  }, [fetchUnreadCount]);

  const markConversationAsRead = useCallback(async (conversationId: string) => {
    try {
      await MessageService.markConversationAsRead(conversationId);
      // Refresh count after marking conversation as read
      await fetchUnreadCount();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to mark conversation as read');
      setError(error);
      console.error('[useUnreadMessages] Error marking conversation as read:', error);
    }
  }, [fetchUnreadCount]);

  // Initial fetch
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUnreadCount();
    } else {
      setCount(0);
      setDetail(null);
      setIsLoading(false);
      setError(null);
    }
  }, [isAuthenticated, user, fetchUnreadCount]);

  // Setup polling
  useEffect(() => {
    if (!autoRefresh || !isAuthenticated || !user) {
      return;
    }

    pollingIntervalRef.current = setInterval(() => {
      fetchUnreadCount();
    }, pollingInterval);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [autoRefresh, isAuthenticated, user, pollingInterval, fetchUnreadCount]);

  // Setup realtime subscription
  useEffect(() => {
    if (!enableRealtime || !isAuthenticated || !user) {
      return;
    }

    realtimeCleanupRef.current = MessageService.setupRealtimeSubscription((newCount) => {
      setCount(newCount);
      // Optionally refresh detail as well
      MessageService.getUnreadMessagesDetail().then(setDetail).catch(console.error);
    });

    return () => {
      if (realtimeCleanupRef.current) {
        realtimeCleanupRef.current();
      }
    };
  }, [enableRealtime, isAuthenticated, user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (realtimeCleanupRef.current) {
        realtimeCleanupRef.current();
      }
    };
  }, []);

  return {
    count,
    isLoading,
    error,
    detail,
    refresh,
    markAsRead,
    markConversationAsRead
  };
};
